/**
 * Observe areas for structural HTML changes to reload components
 */
export class ComponentObserver {
    /**
     * @type {Object}
     */
    options = {
        observerSelector: '[data-component-observe]',
    };

    /**
     * @type {ComponentLoader}
     */
    componentLoader;

    /**
     * @type {WeakMap<Element, MutationObserver>}
     */
    observers = new WeakMap();

    /**
     * @param {ComponentLoader} componentLoader
     * @param {Object} options
     */
    constructor(componentLoader, options = {}) {
        this.options = {...this.options, ...options};

        this.componentLoader = componentLoader;
    }

    /**
     * @param {HTMLElement} parentElement
     */
    createObserversWithin(parentElement) {
        parentElement.querySelectorAll(this.options.observerSelector)
            .forEach(el => this.createObserverForElement(el));
    }

    /**
     * @param {HTMLElement} parentElement
     */
    destroyObserversWithin(parentElement) {
        parentElement.querySelectorAll(this.options.observerSelector)
            .forEach(element => this.destroyObserverForElement(element));
    }

    /**
     * @param {HTMLElement} element
     */
    createObserverForElement(element) {
        if (this.observers.has(element)) {
            throw new Error(
                'Element already has an active component observer, cannot attach another to the same element.'
            );
        }

        const observer = new MutationObserver(
            mutations => mutations.forEach(mutation => this.handleMutation(mutation))
        );
        observer.observe(element, {
            childList: true,
            subtree: true,
        });
        this.observers.set(element, observer);
    }

    destroyObserverForElement(element) {
        if (!this.observers.has(element)) {
            return;
        }

        this.observers.get(element).disconnect();
        this.observers.delete(element);
    }

    /**
     * @param {MutationRecord} mutation
     */
    handleMutation(mutation) {
        mutation.addedNodes.forEach(node => {
            if (!(node instanceof HTMLElement)) {
                return;
            }
            this.componentLoader.loadComponents(node);
        });

        mutation.removedNodes.forEach(node => {
            if (!(node instanceof HTMLElement)) {
                return;
            }
            this.destroyObserverForElement(node);
            this.componentLoader.destroyComponents(node);
        });
    }
}
